import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { Spinner } from "./spinner"

const commonClasses = "cursor-pointer"

const defaultClasses = `inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 ease-out disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive`

const buttonVariants = cva(`${defaultClasses} ${commonClasses}`, {
  variants: {
    variant: {
      default:
        "bg-primary text-white shadow-[var(--shadow-primary)] hover:shadow-[0_6px_20px_var(--shadow-primary)] hover:brightness-110 active:scale-[0.98] active:shadow-sm",
      gradient:
        "bg-gradient-to-r from-gradient-start to-gradient-end text-white shadow-lg hover:shadow-xl hover:brightness-105 active:scale-[0.98]",
      destructive:
        "bg-destructive text-white shadow-[var(--shadow-destructive)] hover:shadow-[0_6px_20px_var(--shadow-destructive)] hover:brightness-110 active:scale-[0.98] focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
      success:
        "bg-success text-success-foreground shadow-[var(--shadow-success)] hover:shadow-[0_6px_20px_var(--shadow-success)] hover:brightness-110 active:scale-[0.98] focus-visible:ring-success/20",
      outline:
        "border border-input bg-background shadow-xs hover:border-primary/50 hover:bg-accent/50 active:scale-[0.98] dark:bg-input/30 dark:border-input dark:hover:bg-input/50 dark:hover:border-primary/50",
      secondary:
        "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80 hover:shadow-sm active:scale-[0.98]",
      ghost:
        "hover:bg-accent hover:text-accent-foreground active:scale-[0.98] dark:hover:bg-accent/50",
      link: "text-primary underline-offset-4 hover:underline",
    },
    size: {
      default: "h-9 px-4 py-2 has-[>svg]:px-3",
      sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
      lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
      xl: "h-12 rounded-lg px-8 text-base has-[>svg]:px-6",
      icon: "size-9",
      "icon-sm": "size-8",
      "icon-lg": "size-10",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
})

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean
      isLoading?: boolean
    }
>(
  (
    {
      className,
      variant,
      size,
      type,
      disabled,
      asChild = false,
      isLoading = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button"

    return (
      <Comp
        ref={ref}
        data-slot="button"
        type={type || "button"}
        disabled={isLoading ? true : disabled}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      >
        {isLoading ? <Spinner className="size-4" /> : props.children}
      </Comp>
    )
  }
)

Button.displayName = "Button"

export { Button, buttonVariants }
